import { normalizeControlType, type CanonicalControlType } from '../api/normalize/controlType';
import type { CustomField, CustomFieldOption } from '../api/types';

/** In-memory value of one custom field, shaped by its canonical control type. */
export type CustomFieldValue =
  | { kind: 'text'; value: string } // text, textarea, date, datetime, number
  | { kind: 'checkbox'; checked: boolean }
  | { kind: 'single'; optionId: number | null; other: Record<number, string> } // select, radio
  | { kind: 'multi'; optionIds: number[]; other: Record<number, string> }; // multiselect

export type CustomFieldValues = Record<number, CustomFieldValue>;
export type ValueKind = CustomFieldValue['kind'] | 'none';

export function valueKindFor(type: CanonicalControlType): ValueKind {
  switch (type) {
    case 'static':
      return 'none';
    case 'checkbox':
      return 'checkbox';
    case 'select':
    case 'radio':
      return 'single';
    case 'multiselect':
      return 'multi';
    default:
      return 'text';
  }
}

/** Active options in display order. */
export function fieldOptions(field: CustomField): CustomFieldOption[] {
  return (field.options ?? [])
    .filter((o) => o.active !== false && typeof o.id === 'number')
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

const TRUTHY = new Set(['true', '1', 'yes', 'on', 'checked']);

export function initialCustomFieldValue(field: CustomField): CustomFieldValue | null {
  const { type } = normalizeControlType(field.control_type);
  const options = fieldOptions(field);
  const defaultValue = field.default_value ?? '';
  const flagged = options.filter((o) => o.default === true);
  const byValue = defaultValue
    ? options.find((o) => o.value === defaultValue || o.label === defaultValue || String(o.id) === defaultValue)
    : undefined;

  switch (valueKindFor(type)) {
    case 'none':
      return null;
    case 'text':
      return { kind: 'text', value: defaultValue };
    case 'checkbox':
      return { kind: 'checkbox', checked: TRUTHY.has(defaultValue.trim().toLowerCase()) };
    case 'single':
      return { kind: 'single', optionId: flagged[0]?.id ?? byValue?.id ?? null, other: {} };
    case 'multi': {
      const ids = flagged.map((o) => o.id).filter((id): id is number => typeof id === 'number');
      if (!ids.length && typeof byValue?.id === 'number') ids.push(byValue.id);
      return { kind: 'multi', optionIds: ids, other: {} };
    }
  }
}

export function defaultCustomFieldValues(fields: CustomField[]): CustomFieldValues {
  const out: CustomFieldValues = {};
  for (const field of fields) {
    if (typeof field.id !== 'number') continue;
    const value = initialCustomFieldValue(field);
    if (value) out[field.id] = value;
  }
  return out;
}

/** "Has the customer answered this?" — drives conditionals and required-field checks. */
export function isValueTruthy(value: CustomFieldValue | undefined): boolean {
  if (!value) return false;
  switch (value.kind) {
    case 'text':
      return value.value.trim() !== '';
    case 'checkbox':
      return value.checked;
    case 'single':
      return value.optionId !== null;
    case 'multi':
      return value.optionIds.length > 0;
  }
}

export function sortByOrdering<T extends { ordering?: number; id?: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0) || (a.id ?? 0) - (b.id ?? 0));
}
